import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { RouteWPSProps } from './types';
import { ArcDroneMarker, ArcPolyline, DestinationMarker, DisabledMarker, DistanceLabel } from '@src/core/controllers';
import { RouteWaypointEntity, RouteWaypointType } from '@src/features/order/pages/upsert-mission-page/entities';
import { useDispatch, useSelector } from 'react-redux';
import { selectEditableRoute, selectWaypoints, setWaypoints } from '@src/features/order/pages/upsert-mission-page/redux';
import { SettingUnitType, useGetSettingByUnitType } from '@src/core/features';
import { arcConvertBetweenUnitTypes } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { getUnitType, useUnitType } from '@src/core/features/settings/units';
import { UnitType } from '@src/core/enums';

const RouteWPS: React.FC<RouteWPSProps> = () => {
  const dispatch = useDispatch();
  const wps = useSelector(selectWaypoints);
  const editableRoute = useSelector(selectEditableRoute);
  const flightAltitude = useGetSettingByUnitType(SettingUnitType.FLIGHT_ALTITUDE);
  const unitType = useUnitType();
  // FLIGHT_ALTITUDE setting now arrives in SI (m); waypoint altitudes are display units.
  const flightAltitudeDisplay = React.useMemo(
    () => arcConvertBetweenUnitTypes(Number(flightAltitude.value), MeasurementType.DISTANCE, UnitType.SI, getUnitType()),
    [flightAltitude.value, unitType],
  );
  const [polylineInstance, setPolylineInstance] = React.useState<google.maps.Polyline | null>(null);

  const onSetAt = React.useCallback((idx: number) => {
    if (!polylineInstance) return;

    const path = polylineInstance.getPath();
    const coords = path.getAt(idx);

    const wpToUpdate = wps[idx];

    if (!wpToUpdate) return;

    const updatedWp: RouteWaypointEntity = {
      ...wpToUpdate,
      latitude: coords.lat(),
      longitude: coords.lng(),
    };

    const newWps = [...wps];
    newWps[idx] = updatedWp;
    dispatch(setWaypoints(newWps));

  }, [polylineInstance, wps]);


  const onInsertAt = React.useCallback((idx: number) => {
    if (!polylineInstance) return;

    const path = polylineInstance.getPath();
    const coords = path.getAt(idx);

    const wp: RouteWaypointEntity = {
      uuid: uuidv4(),
      latitude: coords.lat(),
      longitude: coords.lng(),
      type: RouteWaypointType.WAYPOINT,
      altitude: flightAltitudeDisplay,
    };

    const newWps = [...wps];
    newWps.splice(idx, 0, wp);
    dispatch(setWaypoints(newWps));
  }, [polylineInstance, wps, flightAltitudeDisplay]);


  React.useEffect(() => {

    if (!polylineInstance) return;

    const path = polylineInstance.getPath();

    const listeners = [
      path.addListener('set_at', onSetAt),
      path.addListener('insert_at', onInsertAt),
    ];

    return () => {
      listeners.forEach(listener => listener.remove());
    };

  }, [polylineInstance, onSetAt, onInsertAt]);

  const onLoad = React.useCallback((polyline: google.maps.Polyline) => {
    setPolylineInstance(polyline);
  }, []);

  const onUnmount = React.useCallback(() => {
    setPolylineInstance(null);
  }, []);

  const initialPath = React.useMemo(() => {
    return wps.map((w) => ({
      lat: Number(w.latitude),
      lng: Number(w.longitude),
    }));
  }, [wps]);


  const drone = wps.find((w) => w.type === RouteWaypointType.TAKE_OFF);
  const order = wps.find((w) => w.type === RouteWaypointType.DESTINATION);

  return (
    <React.Fragment>

      <DestinationMarker.pending 
        position={{ 
          lat: Number(order?.latitude), 
          lng: Number(order?.longitude), 
        }} />

      <ArcDroneMarker.idleSelected 
        position={{ 
          lat: Number(drone?.latitude), 
          lng: Number(drone?.longitude), 
        }}  
      />

      <ArcPolyline.future 
        path={initialPath} 
        options={{ 
          editable: editableRoute, 
          clickable: editableRoute,
        }}
        onLoad={onLoad}
        onUnmount={onUnmount} />

      <DistanceLabel path={initialPath} />

      {wps.map((w, idx) => {
        if (w.type === RouteWaypointType.WAYPOINT) return null;
        return (
          <DisabledMarker 
            key={idx} 
            position={{ 
              lat: Number(w.latitude), 
              lng: Number(w.longitude), 
            }} />
        );
      })}
    </React.Fragment>
  );
};

export default RouteWPS;